import type {
    GeneratorConfigV1,
    PricingRule,
    PricingImpact,
} from '../../types/generator-config';
import { randomId } from '../../lib/fieldFactory';
import { CollapsibleBlock } from './CollapsibleBlock';
import { ConditionBuilder } from './ConditionBuilder';
import { ImpactEditor } from './ImpactEditor';
import { Icon } from '../Icon';

interface PricingSectionProps {
    config: GeneratorConfigV1;
    onChange: (updater: (prev: GeneratorConfigV1) => GeneratorConfigV1) => void;
}

export function PricingSection({ config, onChange }: PricingSectionProps) {
    const { pricing, branding } = config;

    const setBase = (amount: number) => {
        onChange((prev) => ({ ...prev, pricing: { ...prev.pricing, baseAmount: amount } }));
    };

    const setFootnote = (footnote: string) => {
        onChange((prev) => ({
            ...prev,
            pricing: { ...prev.pricing, footnote: footnote || undefined },
        }));
    };

    const setRules = (rules: PricingRule[]) => {
        onChange((prev) => ({ ...prev, pricing: { ...prev.pricing, rules } }));
    };

    const addRule = () => {
        const rule: PricingRule = {
            id: randomId(),
            label: 'Nouvelle règle',
            effect: { type: 'add', amount: 0 },
        };
        setRules([...pricing.rules, rule]);
    };

    return (
        <div className="editor-fields">
                <label className="field">
                    <span className="field-label">
                        Prix de base ({branding.currency})
                    </span>
                    <input
                        type="number"
                        min="0"
                        value={pricing.baseAmount}
                        onChange={(e) => setBase(Number(e.target.value) || 0)}
                        placeholder="0"
                    />
                    <small className="impact-hint">
                        Montant affiché avant même toute sélection.
                    </small>
                </label>

                <label className="field">
                    <span className="field-label">Mention affichée sous le prix (optionnelle)</span>
                    <input
                        type="text"
                        value={pricing.footnote ?? ''}
                        onChange={(e) => setFootnote(e.target.value)}
                        placeholder="Estimation indicative hors TVA..."
                        maxLength={200}
                    />
                </label>

                <div className="rules-head">
                    <div>
                        <div className="field-label">Règles globales</div>
                        <small className="impact-hint">
                            S'appliquent après les impacts par champ. Utile pour des
                            promos, des frais fixes ou des marges conditionnelles.
                        </small>
                    </div>
                    <button type="button" className="btn-primary btn-sm" onClick={addRule}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Règle
                    </button>
                </div>

                {pricing.rules.length === 0 ? (
                    <div className="rules-empty">
                        Aucune règle globale pour l'instant. Exemples courants :
                        TVA, remise, frais de déplacement, majoration week-end…
                    </div>
                ) : (
                    <ul className="rules-list">
                        {pricing.rules.map((r) => (
                            <RuleEditor
                                key={r.id}
                                rule={r}
                                config={config}
                                onChange={(next) =>
                                    setRules(pricing.rules.map((x) => (x.id === next.id ? next : x)))
                                }
                                onRemove={() => setRules(pricing.rules.filter((x) => x.id !== r.id))}
                            />
                        ))}
                    </ul>
                )}
        </div>
    );
}

/* ---------- Rule editor ---------- */

interface RuleEditorProps {
    rule: PricingRule;
    config: GeneratorConfigV1;
    onChange: (next: PricingRule) => void;
    onRemove: () => void;
}

function RuleEditor({ rule, config, onChange, onRemove }: RuleEditorProps) {
    return (
        <li className="rule-card">
            <header className="rule-card-head">
                <input
                    type="text"
                    className="rule-card-title"
                    value={rule.label}
                    onChange={(e) => onChange({ ...rule, label: e.target.value })}
                    placeholder="Nom de la règle"
                />
                <button
                    type="button"
                    className="icon-btn icon-btn-danger"
                    onClick={onRemove}
                    aria-label="Supprimer"
                    title="Supprimer la règle"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    </svg>
                </button>
            </header>

            <div className="rule-card-body">
                <div className="rule-block">
                    <div className="rule-block-title">Effet sur le prix</div>
                    <ImpactEditor
                        context="global"
                        impact={rule.effect}
                        onChange={(next) =>
                            onChange({ ...rule, effect: (next ?? { type: 'add', amount: 0 }) as PricingImpact })
                        }
                    />
                </div>

                <CollapsibleBlock
                    title="Appliquer uniquement si..."
                    icon={<Icon name="branch" size={15} />}
                    hasValue={!!rule.condition}
                >
                    <label className="field field-checkbox">
                        <input
                            type="checkbox"
                            checked={!!rule.condition}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    onChange({ ...rule, condition: { fieldId: '' } });
                                } else {
                                    const { condition: _c, ...rest } = rule;
                                    onChange(rest as PricingRule);
                                }
                            }}
                        />
                        <span>Activer une condition pour cette règle</span>
                    </label>
                    {rule.condition && (
                        <ConditionBuilder
                            config={config}
                            rule={rule.condition}
                            onChange={(next) => {
                                if (next === undefined) {
                                    const { condition: _c, ...rest } = rule;
                                    onChange(rest as PricingRule);
                                } else {
                                    onChange({ ...rule, condition: next });
                                }
                            }}
                        />
                    )}
                </CollapsibleBlock>
            </div>
        </li>
    );
}
